import { useState, useCallback, useRef, useEffect } from "react";
import { getUser } from '../auth.js';
import { api } from '../api-client.js';
import { T, Badge, Card, Btn, StatCard, fmtCcy, fmtDate, fmtDateTime, font, mono, dirColor, schemeColor, pmtStatusColor, Inp, Sel, ST } from '../theme.js';
import { PaymentStatus, PaymentDirection, PaymentScheme, SCHEME_CONFIG, APPROVAL_CONFIG, SOFrequency, SOStatus } from '../engines/payment-engine.js';
import { AccountStatus } from '../engines/ledger-engine.js';


const PAYMENTS_NAV = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'send', label: 'Send Payment' },
  { id: 'receive', label: 'Receive' },
  { id: 'approvals', label: 'Approvals' },
  { id: 'beneficiaries', label: 'Beneficiaries' },
  { id: 'standing', label: 'Standing Orders' },
  { id: 'ledger', label: 'Payment Ledger' },
  { id: 'nostro', label: 'Nostro Recon' },
  { id: 'events', label: 'Events' },
];

export default function PaymentsModule({ orchestrator, ledger, eventBus, beneficiaries, standingOrders, refresh: parentRefresh, useGriffin, onGriffinToggle, griffinKey, setGriffinKey, initialContext }) {
  const [view, setView] = useState('dashboard');
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState(null);
  const toastRef = useRef(null);

  useEffect(() => {
    if (initialContext?.category === 'PAYMENT_APPROVAL') setView('approvals');
  }, [initialContext]);

  const refresh = useCallback(() => {
    setTick(t => t + 1);
    if (parentRefresh) parentRefresh();
  }, [parentRefresh]);

  const showToast = useCallback((msg, type = 'success') => {
    if (toastRef.current) clearTimeout(toastRef.current);
    setToast({ msg, type });
    toastRef.current = setTimeout(() => setToast(null), 3500);
  }, []);
  const showError = useCallback(msg => showToast(msg, 'error'), [showToast]);

  const pendingCount = orchestrator.getPendingApprovals().length;

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 1000, padding: '10px 16px',
          background: toast.type === 'error' ? T.redBg : T.greenBg,
          border: `1px solid ${toast.type === 'error' ? T.red : T.green}40`,
          borderRadius: 6, fontSize: 12, maxWidth: 420, fontFamily: font }}>
          <span style={{ color: toast.type === 'error' ? T.red : T.green, marginRight: 8 }}>{toast.type === 'error' ? '✗' : '✓'}</span>{toast.msg}
        </div>
      )}

      <GriffinSandboxPanel useGriffin={useGriffin} onGriffinToggle={onGriffinToggle} griffinKey={griffinKey} setGriffinKey={setGriffinKey} />

      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `1px solid ${T.border}`, overflowX: 'auto' }}>
        {PAYMENTS_NAV.map(n => (
          <button key={n.id} onClick={() => setView(n.id)} style={{
            padding: '8px 14px', fontSize: 12, fontWeight: view === n.id ? 600 : 400,
            color: view === n.id ? T.blue : T.textSecondary,
            background: 'transparent', border: 'none',
            borderBottom: view === n.id ? `2px solid ${T.blue}` : '2px solid transparent',
            cursor: 'pointer', fontFamily: font, marginBottom: -1, whiteSpace: 'nowrap',
          }}>
            {n.label}
            {n.id === 'approvals' && pendingCount > 0 && <span style={{ marginLeft: 6, background: T.amber, color: '#fff', borderRadius: 8, padding: '0 5px', fontSize: 9, fontWeight: 700, lineHeight: '16px', display: 'inline-block' }}>{pendingCount}</span>}
          </button>
        ))}
      </div>

      {view === 'dashboard' && <PaymentsDashboard orchestrator={orchestrator} ledger={ledger} key={tick} />}
      {view === 'send' && <SendPaymentView orchestrator={orchestrator} ledger={ledger} beneficiaries={beneficiaries} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {view === 'receive' && <ReceivePaymentView orchestrator={orchestrator} ledger={ledger} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {view === 'approvals' && <ApprovalsView orchestrator={orchestrator} ledger={ledger} beneficiaries={beneficiaries} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {view === 'beneficiaries' && <BeneficiariesView beneficiaries={beneficiaries} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {view === 'standing' && <StandingOrdersView standingOrders={standingOrders} orchestrator={orchestrator} ledger={ledger} beneficiaries={beneficiaries} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {view === 'ledger' && <PaymentLedgerView orchestrator={orchestrator} ledger={ledger} key={tick} />}
      {view === 'nostro' && <NostroReconView orchestrator={orchestrator} ledger={ledger} key={tick} />}
      {view === 'events' && <PaymentEventsView eventBus={eventBus} key={tick} />}
    </div>
  );
}


// ============================================================
// GRIFFIN SANDBOX TOGGLE
// ============================================================

function GriffinSandboxPanel({ useGriffin, onGriffinToggle, griffinKey, setGriffinKey }) {
  const [localKey, setLocalKey] = useState(griffinKey || '');

  const handleToggle = () => {
    if (useGriffin) {
      onGriffinToggle(false);
    } else if (localKey.trim()) {
      setGriffinKey(localKey.trim());
      onGriffinToggle(true, localKey.trim());
    }
  };

  const handleKeyChange = (e) => {
    setLocalKey(e.target.value);
    if (useGriffin && e.target.value.trim()) {
      setGriffinKey(e.target.value.trim());
      onGriffinToggle(true, e.target.value.trim());
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
      padding: '8px 12px', borderRadius: 6,
      background: useGriffin ? `${T.green}10` : T.surface,
      border: `1px solid ${useGriffin ? `${T.green}30` : T.border}`,
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: '50%',
        background: useGriffin ? T.green : T.textMuted,
        flexShrink: 0,
      }} />
      <span style={{ fontSize: 11, color: useGriffin ? T.green : T.textMuted, fontWeight: 600, whiteSpace: 'nowrap' }}>
        {useGriffin ? 'Live — Griffin Sandbox' : 'Simulated'}
      </span>

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginLeft: 4 }}>
        <div onClick={handleToggle} style={{
          width: 32, height: 18, borderRadius: 9, position: 'relative', cursor: 'pointer',
          background: useGriffin ? T.green : T.border,
          transition: 'background 0.2s',
        }}>
          <div style={{
            width: 14, height: 14, borderRadius: '50%', background: '#fff',
            position: 'absolute', top: 2,
            left: useGriffin ? 16 : 2,
            transition: 'left 0.2s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }} />
        </div>
        <span style={{ fontSize: 11, color: T.textSecondary }}>Griffin Sandbox</span>
      </label>

      {(!useGriffin || true) && (
        <input
          type="password"
          placeholder="Griffin API key"
          value={localKey}
          onChange={handleKeyChange}
          style={{
            flex: 1, maxWidth: 260, padding: '4px 8px', fontSize: 11,
            background: T.bg, color: T.text, border: `1px solid ${T.border}`,
            borderRadius: 4, fontFamily: font, outline: 'none',
          }}
        />
      )}
    </div>
  );
}


// ============================================================
// DASHBOARD
// ============================================================

function PaymentsDashboard({ orchestrator, ledger }) {
  const payments = orchestrator.getAllPayments();
  const settled = payments.filter(p => p.status === PaymentStatus.SETTLED);
  const inFlight = payments.filter(p => ![PaymentStatus.SETTLED, PaymentStatus.FAILED, PaymentStatus.REJECTED, PaymentStatus.RETURNED].includes(p.status));
  const outbound = settled.filter(p => p.direction === PaymentDirection.OUTBOUND);
  const inbound = settled.filter(p => p.direction === PaymentDirection.INBOUND);
  const pendingApprovals = orchestrator.getPendingApprovals();
  const dailyLimits = orchestrator.getDailyLimitSummary();

  return (
    <div>
      <ST>Payment Operations</ST>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label="Total Payments" value={payments.length} sub={`${settled.length} settled`} color={T.blue} />
        <StatCard label="Outbound" value={outbound.length} sub={fmtCcy(outbound.reduce((s, p) => s + p.amount, 0))} color={T.red} />
        <StatCard label="Inbound" value={inbound.length} sub={fmtCcy(inbound.reduce((s, p) => s + p.amount, 0))} color={T.green} />
        <StatCard label="In Flight" value={inFlight.length} color={T.amber} />
        <StatCard label="Pending Approval" value={pendingApprovals.length} color={pendingApprovals.length > 0 ? T.amber : T.textMuted} />
      </div>

      {/* Daily Limit Utilisation */}
      <Card style={{ marginBottom: 16 }}>
        <ST>Daily Limit Utilisation</ST>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {dailyLimits.filter(d => d.limit).map(d => (
            <div key={d.scheme} style={{ flex: 1, minWidth: 160 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>{d.scheme}</span>
                <span style={{ color: T.textMuted, fontFamily: mono }}>{fmtCcy(d.used)} / {fmtCcy(d.limit)}</span>
              </div>
              <div style={{ height: 6, background: T.surfaceAlt, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(d.pct, 100)}%`, background: d.pct > 80 ? T.red : d.pct > 50 ? T.amber : T.green, borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2, textAlign: 'right' }}>{d.pct.toFixed(0)}% used</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Pending Approvals */}
      {pendingApprovals.length > 0 && (
        <Card style={{ marginBottom: 16, border: `1px solid ${T.amberBorder}`, background: T.amberBg }}>
          <ST>{pendingApprovals.length} Payment{pendingApprovals.length !== 1 ? 's' : ''} Awaiting Dual Authorisation</ST>
          <DataTableCompact
            headers={['Ref', 'Scheme', 'Amount', 'Initiated By', 'Threshold']}
            rows={pendingApprovals.map(p => {
              const initiator = p.statusHistory.find(h => h.status === 'INITIATED')?.by || '—';
              return [
                <span style={{ fontWeight: 600, fontSize: 11, fontFamily: mono }}>{p.reference}</span>,
                <Badge color={schemeColor(p.scheme)}>{p.scheme}</Badge>,
                <span style={{ fontWeight: 700, fontFamily: mono, color: T.text }}>{fmtCcy(p.amount, p.currency)}</span>,
                <span style={{ fontSize: 12 }}>{initiator}</span>,
                <span style={{ fontSize: 11, color: T.textMuted }}>≥ {fmtCcy(p.approvalThreshold || 0, p.currency)}</span>,
              ];
            })}
          />
        </Card>
      )}

      <Card>
        <ST>Recent Payments</ST>
        {payments.length === 0
          ? <div style={{ color: T.textMuted, fontSize: 13, padding: 24, textAlign: 'center' }}>No payments yet. Use Send Payment or Receive to create some.</div>
          : <DataTableCompact
              headers={['Ref', 'Dir', 'Scheme', 'Amount', 'Status', 'Counterparty', 'Time']}
              rows={[...payments].reverse().slice(0, 15).map(p => [
                <span style={{ fontWeight: 600, fontSize: 11, fontFamily: mono }}>{p.reference}</span>,
                <Badge color={dirColor(p.direction)}>{p.direction}</Badge>,
                <Badge color={schemeColor(p.scheme)}>{p.scheme}</Badge>,
                <span style={{ fontWeight: 600, fontFamily: mono }}>{fmtCcy(p.amount, p.currency)}</span>,
                <Badge color={pmtStatusColor(p.status)}>{p.status}</Badge>,
                <span style={{ fontSize: 12, color: T.textSecondary }}>{p.beneficiary?.name || p.sender?.name || '—'}</span>,
                <span style={{ fontSize: 10, color: T.textMuted }}>{new Date(p.createdAt).toLocaleTimeString()}</span>,
              ])}
            />
        }
      </Card>
    </div>
  );
}

// Compact table used across multiple views
function DataTableCompact({ headers, rows }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: font }}>
        <thead><tr>{headers.map((h, i) => <th key={i} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, i) => <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>{row.map((cell, j) => <td key={j} style={{ padding: '6px 8px' }}>{cell}</td>)}</tr>)}
          {rows.length === 0 && <tr><td colSpan={headers.length} style={{ padding: 20, textAlign: 'center', color: T.textMuted }}>No data</td></tr>}
        </tbody>
      </table>
    </div>
  );
}


// ============================================================
// SEND PAYMENT
// ============================================================

function SendPaymentView({ orchestrator, ledger, beneficiaries, refresh, showToast, showError }) {
  const custAccs = ledger.getAllAccounts({ internal: false }).filter(a => a.status === AccountStatus.ACTIVE);
  const savedBens = beneficiaries ? beneficiaries.getAll() : [];
  const [form, setForm] = useState({ sourceAccountId: '', amount: '', scheme: 'FPS', beneficiaryName: '', sortCode: '', accountNumber: '', bic: '', iban: '', reference: '', savedBeneficiaryId: '' });
  const [validationResult, setValidationResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [saveBeneficiary, setSaveBeneficiary] = useState(false);

  const schemeConfig = SCHEME_CONFIG[form.scheme];
  const selectedAcc = form.sourceAccountId ? ledger.getAccount(form.sourceAccountId) : null;
  const selectedBal = selectedAcc ? ledger.getBalance(selectedAcc.id) : null;
  const approvalThreshold = APPROVAL_CONFIG.getThreshold(form.scheme);
  const amount = parseFloat(form.amount) || 0;
  const willNeedApproval = APPROVAL_CONFIG.enabled && APPROVAL_CONFIG.requiresDualAuth(form.scheme, amount);

  const handleSelectBeneficiary = (benId) => {
    if (!benId) {
      setForm({ ...form, savedBeneficiaryId: '', beneficiaryName: '', sortCode: '', accountNumber: '', bic: '', iban: '' });
      return;
    }
    const ben = beneficiaries.get(benId);
    if (ben) {
      setForm({
        ...form,
        savedBeneficiaryId: benId,
        beneficiaryName: ben.name,
        sortCode: ben.sortCode || '',
        accountNumber: ben.accountNumber || '',
        bic: ben.bic || '',
        iban: ben.iban || '',
        scheme: ben.scheme || form.scheme,
      });
    }
  };

  const handleSend = async () => {
    setProcessing(true); setValidationResult(null);
    try {
      const beneficiary = { name: form.beneficiaryName };
      if (form.sortCode) beneficiary.sortCode = form.sortCode.replace(/-/g, '');
      if (form.accountNumber) beneficiary.accountNumber = form.accountNumber;
      if (form.bic) beneficiary.bic = form.bic;
      if (form.iban) beneficiary.iban = form.iban;

      const pmt = await orchestrator.initiatePayment({
        sourceAccountId: form.sourceAccountId,
        amount: parseFloat(form.amount),
        currency: selectedAcc.currency,
        scheme: form.scheme,
        beneficiary,
        reference: form.reference || `PMT-${Date.now()}`,
        initiatedBy: 'rm_sarah_j',
      });

      if (pmt.status === PaymentStatus.FAILED) {
        setValidationResult({ valid: false, errors: ['Hold placement failed — insufficient funds'] });
        setProcessing(false); return;
      }

      const vResult = await orchestrator.validatePayment(pmt.id);
      setValidationResult(vResult);

      if (vResult.valid) {
        // Record beneficiary usage
        if (form.savedBeneficiaryId && beneficiaries) await beneficiaries.recordUsage(form.savedBeneficiaryId);
        // Save new beneficiary if requested
        if (saveBeneficiary && !form.savedBeneficiaryId && beneficiaries) {
          await beneficiaries.add({ name: form.beneficiaryName, sortCode: form.sortCode, accountNumber: form.accountNumber, bic: form.bic, iban: form.iban, scheme: form.scheme, createdBy: 'rm_sarah_j' });
        }

        if (vResult.requiresApproval) {
          await refresh();
          showToast(`Payment queued for dual authorisation (${fmtCcy(pmt.amount, pmt.currency)} via ${pmt.scheme}). Awaiting second approver.`, 'success');
        } else {
          const result = await orchestrator.submitPayment(pmt.id);
          await refresh();
          if (result.status === PaymentStatus.SETTLED) {
            showToast(`Payment settled: ${fmtCcy(pmt.amount, pmt.currency)} via ${pmt.scheme}${pmt.feeAmount ? ` (fee: ${fmtCcy(pmt.feeAmount, pmt.currency)})` : ''}`);
          } else if (result.status === PaymentStatus.REJECTED) {
            showError(`Rejected by scheme: ${result.rejectionReason}`);
          } else {
            showToast(`Payment ${result.status}`);
          }
        }
      } else {
        refresh();
        showError(`Validation failed: ${vResult.errors.length} error(s)`);
      }
    } catch (e) { showError(e.message); }
    setProcessing(false);
  };

  return (
    <div>
      <ST>Send Payment</ST>
      <div style={{ display: 'flex', gap: 16 }}>
        <Card style={{ flex: 1, maxWidth: 480 }}>
          <Sel label="Source Account" value={form.sourceAccountId} onChange={v => setForm({ ...form, sourceAccountId: v })}
            options={[{ value: '', label: 'Select account...' }, ...custAccs.map(a => { const b = ledger.getBalance(a.id); return { value: a.id, label: `${a.accountNumber} — ${a.name} (${fmtCcy(b.available, a.currency)})` }; })]} />
          <Sel label="Payment Scheme" value={form.scheme} onChange={v => setForm({ ...form, scheme: v })}
            options={Object.entries(PaymentScheme).map(([k, v]) => ({ value: v, label: `${v} — ${SCHEME_CONFIG[v].name}` }))} />
          <Inp label="Amount" type="number" value={form.amount} onChange={v => setForm({ ...form, amount: v })} placeholder="0.00" />
          <Inp label="Reference" value={form.reference} onChange={v => setForm({ ...form, reference: v })} placeholder="INV-2024-001" />

          {/* Saved Beneficiary Selector */}
          {savedBens.length > 0 && (
            <div style={{ margin: '12px 0 4px', padding: '10px 12px', background: T.blueBg, border: `1px solid ${T.blueBorder}`, borderRadius: 6 }}>
              <Sel label="Quick Select — Saved Beneficiary" value={form.savedBeneficiaryId} onChange={handleSelectBeneficiary}
                options={[{ value: '', label: 'Enter manually…' }, ...savedBens.map(b => ({ value: b.id, label: `${b.nickname}${b.scheme ? ` (${b.scheme})` : ''}` }))]} />
            </div>
          )}

          <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, margin: '16px 0 8px', borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>Beneficiary Details</div>
          <Inp label="Beneficiary Name" value={form.beneficiaryName} onChange={v => setForm({ ...form, beneficiaryName: v })} placeholder="Widget Supplier Ltd" />
          {(schemeConfig?.requiresSortCode || form.scheme === 'INTERNAL') && <Inp label="Sort Code" value={form.sortCode} onChange={v => setForm({ ...form, sortCode: v })} placeholder="090128" />}
          {(schemeConfig?.requiresAccountNumber || form.scheme === 'INTERNAL') && <Inp label="Account Number" value={form.accountNumber} onChange={v => setForm({ ...form, accountNumber: v })} placeholder="31926819" />}
          {schemeConfig?.requiresBIC && <Inp label="BIC / SWIFT Code" value={form.bic} onChange={v => setForm({ ...form, bic: v })} placeholder="LOYDGB2L" />}
          {schemeConfig?.requiresIBAN && <Inp label="IBAN" value={form.iban} onChange={v => setForm({ ...form, iban: v })} placeholder="GB29NWBK60161331926819" />}

          {!form.savedBeneficiaryId && form.beneficiaryName && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textSecondary, marginTop: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={saveBeneficiary} onChange={e => setSaveBeneficiary(e.target.checked)} />
              Save this beneficiary for future payments
            </label>
          )}

          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <Btn onClick={handleSend} disabled={processing || !form.sourceAccountId || !form.amount}>
              {processing ? 'Processing…' : willNeedApproval ? 'Initiate → Validate → Queue for Approval' : 'Initiate → Validate → Submit'}
            </Btn>
            {willNeedApproval && <span style={{ fontSize: 11, color: T.amber, fontWeight: 500 }}>⚠ Dual auth required (≥ {fmtCcy(approvalThreshold)})</span>}
          </div>
        </Card>

        <div style={{ flex: 1, maxWidth: 380 }}>
          {selectedAcc && selectedBal && (
            <Card style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Account Summary</div>
              <div style={{ fontSize: 13, lineHeight: 2 }}>
                <div>Posted: <span style={{ fontWeight: 600, fontFamily: mono }}>{fmtCcy(selectedBal.posted, selectedAcc.currency)}</span></div>
                <div>Available: <span style={{ fontWeight: 600, fontFamily: mono, color: T.green }}>{fmtCcy(selectedBal.available, selectedAcc.currency)}</span></div>
                <div>Holds: <span style={{ fontFamily: mono, color: T.amber }}>{fmtCcy(selectedBal.posted - selectedBal.available, selectedAcc.currency)}</span></div>
              </div>
            </Card>
          )}

          <Card style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Scheme: {schemeConfig?.name}</div>
            <div style={{ fontSize: 12, lineHeight: 2, color: T.textSecondary }}>
              {schemeConfig?.maxAmount && <div>Max amount: {schemeConfig.maxAmount.toLocaleString()}</div>}
              {schemeConfig?.cutoffTime && <div>Cutoff: {schemeConfig.cutoffTime}</div>}
              {schemeConfig?.currency && <div>Currency: {schemeConfig.currency}</div>}
              {schemeConfig?.approvalThreshold && <div style={{ color: T.amber }}>Dual auth threshold: {fmtCcy(schemeConfig.approvalThreshold)}</div>}
            </div>
          </Card>

          {validationResult && !validationResult.valid && (
            <Card style={{ background: T.redBg, border: `1px solid ${T.redBorder}` }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.red, marginBottom: 6 }}>Validation Failed</div>
              {validationResult.errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: T.red, marginBottom: 3 }}>• {e}</div>)}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// RECEIVE PAYMENT (simulate inbound)
// ============================================================

function ReceivePaymentView({ orchestrator, ledger, refresh, showToast, showError }) {
  const custAccs = ledger.getAllAccounts({ internal: false }).filter(a => a.status === AccountStatus.ACTIVE);
  const [form, setForm] = useState({ targetAccountId: '', amount: '', scheme: 'FPS', senderName: '', sortCode: '', accountNumber: '', reference: '' });

  const handleReceive = async () => {
    try {
      const acc = ledger.getAccount(form.targetAccountId);
      await orchestrator.receiveInboundPayment({
        targetAccountId: form.targetAccountId,
        amount: parseFloat(form.amount),
        currency: acc.currency,
        scheme: form.scheme,
        sender: { name: form.senderName, sortCode: form.sortCode, accountNumber: form.accountNumber },
        reference: form.reference || `IN-${Date.now()}`,
      });
      await refresh();
      showToast(`Inbound ${form.scheme} received: ${fmtCcy(parseFloat(form.amount), acc.currency)}`);
      setForm({ ...form, amount: '', senderName: '', reference: '' });
    } catch (e) { showError(e.message); }
  };

  return (
    <div>
      <ST>Simulate Inbound Payment</ST>
      <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>In production, inbound payments arrive via webhook from the scheme provider (Modulr/ClearBank). This simulates that arrival.</p>
      <Card style={{ maxWidth: 480 }}>
        <Sel label="Target Account" value={form.targetAccountId} onChange={v => setForm({ ...form, targetAccountId: v })}
          options={[{ value: '', label: 'Select...' }, ...custAccs.map(a => ({ value: a.id, label: `${a.accountNumber} — ${a.name}` }))]} />
        <Sel label="Scheme" value={form.scheme} onChange={v => setForm({ ...form, scheme: v })}
          options={[{ value: 'FPS', label: 'FPS' }, { value: 'CHAPS', label: 'CHAPS' }, { value: 'SWIFT', label: 'SWIFT' }]} />
        <Inp label="Amount" type="number" value={form.amount} onChange={v => setForm({ ...form, amount: v })} placeholder="0.00" />
        <Inp label="Sender Name" value={form.senderName} onChange={v => setForm({ ...form, senderName: v })} placeholder="Client Co Ltd" />
        <Inp label="Reference" value={form.reference} onChange={v => setForm({ ...form, reference: v })} placeholder="INV-2024-099" />
        <Btn onClick={handleReceive} disabled={!form.targetAccountId || !form.amount}>Receive Payment</Btn>
      </Card>
    </div>
  );
}


// ============================================================
// PAYMENT LEDGER (list + detail panel)
// ============================================================

function PaymentLedgerView({ orchestrator, ledger }) {
  const payments = [...orchestrator.getAllPayments()].reverse();
  const [selected, setSelected] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [showReturn, setShowReturn] = useState(false);
  const [, setTick] = useState(0);

  const handleReturn = async () => {
    try {
      await orchestrator.returnPayment(selected.id, { reason: returnReason || 'Payment returned', returnedBy: 'rm_sarah_j' });
      setSelected(orchestrator.getPayment(selected.id));
      setShowReturn(false);
      setReturnReason('');
      setTick(t => t + 1);
    } catch (e) { console.error(e); }
  };

  return (
    <div>
      <ST>Payment Ledger</ST>
      <div style={{ display: 'flex', gap: 16 }}>
        <Card style={{ flex: 2 }}>
          <DataTableCompact
            headers={['Ref', 'Dir', 'Scheme', 'Amount', 'Fee', 'Status', 'Counterparty']}
            rows={payments.map(p => [
              <span style={{ fontWeight: 600, fontSize: 11, fontFamily: mono, cursor: 'pointer', color: selected?.id === p.id ? T.blue : T.text }} onClick={() => setSelected(p)}>{p.reference}</span>,
              <Badge color={dirColor(p.direction)}>{p.direction}</Badge>,
              <Badge color={schemeColor(p.scheme)}>{p.scheme}</Badge>,
              <span style={{ fontWeight: 600, fontFamily: mono }}>{fmtCcy(p.amount, p.currency)}</span>,
              <span style={{ color: T.textMuted, fontFamily: mono }}>{p.feeAmount > 0 ? fmtCcy(p.feeAmount, p.currency) : '—'}</span>,
              <Badge color={pmtStatusColor(p.status)}>{p.status}</Badge>,
              <span style={{ fontSize: 12, color: T.textSecondary }}>{p.beneficiary?.name || p.sender?.name}</span>,
            ])}
          />
        </Card>

        {selected && (
          <Card style={{ flex: 1, maxWidth: 340 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Payment Detail</div>
            <div style={{ fontSize: 12, lineHeight: 2 }}>
              <div><span style={{ color: T.textMuted }}>ID:</span> <span style={{ fontFamily: mono, fontSize: 11 }}>{selected.id.slice(0, 18)}…</span></div>
              <div><span style={{ color: T.textMuted }}>Reference:</span> {selected.reference}</div>
              <div><span style={{ color: T.textMuted }}>Amount:</span> <span style={{ fontWeight: 700, fontFamily: mono }}>{fmtCcy(selected.amount, selected.currency)}</span></div>
              <div><span style={{ color: T.textMuted }}>Fee:</span> {selected.feeAmount > 0 ? fmtCcy(selected.feeAmount, selected.currency) : 'None'}</div>
              <div><span style={{ color: T.textMuted }}>Scheme:</span> <Badge color={schemeColor(selected.scheme)}>{selected.scheme}</Badge></div>
              <div><span style={{ color: T.textMuted }}>Status:</span> <Badge color={pmtStatusColor(selected.status)}>{selected.status}</Badge></div>
              {(selected.initiatedBy || selected.statusHistory?.[0]?.by) && <div><span style={{ color: T.textMuted }}>Initiated by:</span> {selected.initiatedBy || selected.statusHistory[0].by}</div>}
              {selected.approvedBy && <div><span style={{ color: T.green }}>Approved by:</span> {selected.approvedBy}</div>}
              {selected.schemeTransactionId && <div><span style={{ color: T.textMuted }}>Scheme TX:</span> <span style={{ fontFamily: mono, fontSize: 11 }}>{selected.schemeTransactionId.slice(0, 16)}…</span></div>}
              {selected.rejectionReason && <div><span style={{ color: T.red }}>Rejected:</span> {selected.rejectionReason}</div>}
              {selected.returnReason && <div><span style={{ color: T.amber }}>Return reason:</span> {selected.returnReason}</div>}
              {selected.holdId && <div><span style={{ color: T.textMuted }}>Hold:</span> <span style={{ fontFamily: mono, fontSize: 11 }}>{selected.holdId.slice(0, 14)}…</span></div>}
              {selected.settlementTxnId && <div><span style={{ color: T.textMuted }}>Ledger TX:</span> <span style={{ fontFamily: mono, fontSize: 11 }}>{selected.settlementTxnId.slice(0, 14)}…</span></div>}
            </div>

            {/* Return action */}
            {(selected.status === PaymentStatus.SETTLED || selected.status === PaymentStatus.ACCEPTED) && (
              <div style={{ marginTop: 10, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                {!showReturn ? (
                  <Btn onClick={() => setShowReturn(true)} variant="danger" small>Return Payment</Btn>
                ) : (
                  <div>
                    <div style={{ fontSize: 11, color: T.red, fontWeight: 600, marginBottom: 6 }}>Return this payment</div>
                    <input style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.redBorder}`, fontSize: 12, fontFamily: font, marginBottom: 6, boxSizing: 'border-box' }} placeholder="Return reason…" value={returnReason} onChange={e => setReturnReason(e.target.value)} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn onClick={handleReturn} variant="danger" small>Confirm Return</Btn>
                      <Btn onClick={() => { setShowReturn(false); setReturnReason(''); }} variant="ghost" small>Cancel</Btn>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 12, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>State History</div>
              {selected.statusHistory.map((h, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 11 }}>
                  <Badge color={pmtStatusColor(h.status)}>{h.status}</Badge>
                  <span style={{ color: T.textMuted, fontSize: 10 }}>{new Date(h.at).toLocaleTimeString()}</span>
                  {h.reason && <span style={{ color: T.textMuted, fontSize: 10 }}>— {h.reason.slice(0, 40)}</span>}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}


// ============================================================
// APPROVALS — Dual authorisation queue
// ============================================================

function ApprovalExpandedPanel({ payment, ledger, beneficiaries, allPayments, section, onApprove, onReject, onRefer }) {
  const [customerData, setCustomerData] = useState(null);
  const [recentEntries, setRecentEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [referForm, setReferForm] = useState(null); // null=hidden, or { team, reason }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c360, entries] = await Promise.all([
          payment.metadata?.customerId ? api.get(`/crm/customer360/${payment.metadata.customerId}/enhanced`).catch(() => null) : null,
          payment.sourceAccountId ? api.get(`/ledger/entries`).then(all => all.filter(e => e.accountId === payment.sourceAccountId).slice(-5).reverse()).catch(() => []) : [],
        ]);
        if (!cancelled) { setCustomerData(c360); setRecentEntries(entries); setLoading(false); }
      } catch { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [payment]);

  const bene = payment.beneficiary;
  const registeredBene = beneficiaries?.getAll?.()?.find(b =>
    b.accountNumber === bene?.accountNumber && b.sortCode === bene?.sortCode
  );
  const duplicates = allPayments.filter(p =>
    p.id !== payment.id &&
    p.status !== 'FAILED' && p.status !== 'REJECTED' && p.status !== 'SETTLED' && p.status !== 'RETURNED' &&
    p.beneficiary?.accountNumber === bene?.accountNumber &&
    p.beneficiary?.sortCode === bene?.sortCode &&
    p.metadata?.customerId === payment.metadata?.customerId
  );

  const panelStyle = { borderTop: `1px solid ${T.border}`, marginTop: 12, paddingTop: 12 };
  const labelStyle = { fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 };

  return (
    <div style={panelStyle}>
      {loading ? <div style={{ fontSize: 12, color: T.textMuted, padding: 8 }}>Loading details...</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
          {/* Customer summary */}
          <div>
            <div style={labelStyle}>Customer</div>
            {customerData ? (
              <div style={{ fontSize: 12 }}>
                <div style={{ fontWeight: 500 }}>{customerData.customer?.legalName || '—'}</div>
                {customerData.riskRating && <Badge color={customerData.riskRating === 'HIGH' ? 'red' : customerData.riskRating === 'MEDIUM' ? 'amber' : 'green'}>{customerData.riskRating}</Badge>}
              </div>
            ) : <div style={{ fontSize: 12, color: T.textMuted }}>—</div>}
          </div>
          {/* Beneficiary */}
          <div>
            <div style={labelStyle}>Beneficiary</div>
            <div style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 500 }}>{bene?.name || '—'}</div>
              <div style={{ fontFamily: mono, color: T.textSecondary }}>{bene?.sortCode || ''} {bene?.accountNumber || ''}</div>
              {registeredBene ? <Badge color="green">Registered</Badge> : <Badge color="gray">Unregistered</Badge>}
            </div>
          </div>
        </div>
      )}

      {/* Duplicate warning */}
      {duplicates.length > 0 && (
        <div style={{ background: T.amberBg, border: `1px solid ${T.amberBorder}`, borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: T.amber, fontWeight: 500 }}>
          Warning: {duplicates.length} other pending payment{duplicates.length > 1 ? 's' : ''} to this beneficiary
        </div>
      )}

      {/* Recent transactions */}
      {recentEntries && recentEntries.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>Recent Account Activity</div>
          <div style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {recentEntries.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, color: T.textSecondary }}>
                <span style={{ fontFamily: mono, width: 80 }}>{fmtDate(e.date || e.createdAt)}</span>
                <span style={{ flex: 1 }}>{e.description || e.type || '—'}</span>
                <span style={{ fontFamily: mono, fontWeight: 500, color: e.direction === 'DEBIT' ? T.red : T.green }}>{fmtCcy(e.amount, e.currency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 2: who can approve */}
      {section === 'own' && (
        <div style={{ background: T.surfaceAlt, borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: T.textSecondary }}>
          Requires approval from another authorised user. Contact <strong>james.wilson</strong> or <strong>ops.admin</strong> to approve.
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {section === 'actionable' && (
          <>
            <Btn onClick={() => onApprove(payment.id)} variant="primary" small>
              {payment.status === 'INITIATED' && payment.metadata?.via === 'customer-portal' ? 'Validate & Submit' : 'Approve & Submit'}
            </Btn>
            {!referForm && <Btn onClick={() => setReferForm({ team: 'compliance', reason: '' })} style={{ background: T.amber, borderColor: T.amber, color: '#fff' }} small>Refer for Review</Btn>}
          </>
        )}
        {!showReject && !referForm ? (
          <Btn onClick={() => setShowReject(true)} variant={section === 'actionable' ? 'danger' : 'secondary'} small>{section === 'own' ? 'Cancel' : 'Reject'}</Btn>
        ) : showReject ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
            <input style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font }} placeholder={section === 'own' ? 'Cancellation reason…' : 'Rejection reason…'} value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
            <Btn onClick={() => { onReject(payment.id, rejectReason); setShowReject(false); }} variant="danger" small>Confirm</Btn>
            <Btn onClick={() => setShowReject(false)} variant="ghost" small>Cancel</Btn>
          </div>
        ) : null}
      </div>

      {/* Referral form */}
      {referForm && (
        <div style={{ marginTop: 10, padding: 12, background: T.surfaceAlt, borderRadius: 6, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Refer for Review</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: T.textSecondary }}>Refer to:</label>
            <select value={referForm.team} onChange={e => setReferForm(f => ({ ...f, team: e.target.value }))} style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font }}>
              <option value="compliance">Compliance</option>
              <option value="fraud">Fraud</option>
              <option value="operations">Operations</option>
            </select>
          </div>
          <textarea
            placeholder="What concerns you? (required)"
            value={referForm.reason}
            onChange={e => setReferForm(f => ({ ...f, reason: e.target.value }))}
            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, minHeight: 60, resize: 'vertical', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Btn onClick={() => { if (referForm.reason.trim()) { onRefer(payment.id, referForm.team, referForm.reason); setReferForm(null); } }} variant="primary" small disabled={!referForm.reason.trim()}>Submit Referral</Btn>
            <Btn onClick={() => setReferForm(null)} variant="ghost" small>Cancel</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function ApprovalsView({ orchestrator, ledger, beneficiaries, refresh, showToast, showError }) {
  const pending = orchestrator.getPendingApprovals();
  const referred = orchestrator.getReferredPayments();
  const allPayments = orchestrator.getAllPayments();
  const recentApproved = allPayments.filter(p => p.approvedBy).slice(-10).reverse();
  const [expandedId, setExpandedId] = useState(null);

  const currentUsername = getUser().username;
  const actionable = pending.filter(p => {
    const initiator = p.statusHistory.find(h => h.status === 'INITIATED')?.by;
    return initiator !== currentUsername || (p.status === 'INITIATED' && p.metadata?.via === 'customer-portal');
  });
  const own = pending.filter(p => {
    const initiator = p.statusHistory.find(h => h.status === 'INITIATED')?.by;
    return initiator === currentUsername && !(p.status === 'INITIATED' && p.metadata?.via === 'customer-portal');
  });

  const handleApprove = async (paymentId) => {
    try {
      const payment = orchestrator.getPayment(paymentId);
      if (payment?.status === 'INITIATED') {
        const valResult = await orchestrator.validatePayment(paymentId);
        const validated = valResult?.payment || orchestrator.getPayment(paymentId);
        if (validated?.status === 'PENDING_APPROVAL') await orchestrator.approvePayment(paymentId, currentUsername);
      } else {
        await orchestrator.approvePayment(paymentId, currentUsername);
      }
      const result = await orchestrator.submitPayment(paymentId);
      setExpandedId(null);
      await refresh();
      if (result.status === PaymentStatus.SETTLED) showToast(`Approved and settled: ${fmtCcy(result.amount, result.currency)} via ${result.scheme}`);
      else if (result.status === PaymentStatus.REJECTED) showError(`Approved but rejected by scheme: ${result.rejectionReason}`);
      else showToast(`Approved: ${result.status}`);
    } catch (e) { showError(e.message); }
  };

  const handleReject = async (paymentId, reason) => {
    try {
      const payment = orchestrator.getPayment(paymentId);
      if (payment?.status === 'INITIATED') {
        await orchestrator.validatePayment(paymentId);
        await orchestrator.rejectApproval(paymentId, currentUsername, reason || 'Rejected during bank review');
      } else {
        await orchestrator.rejectApproval(paymentId, currentUsername, reason || 'Rejected during review');
      }
      setExpandedId(null);
      await refresh();
      showToast('Payment rejected. Hold released.');
    } catch (e) { showError(e.message); }
  };

  const handleRefer = async (paymentId, team, reason) => {
    try {
      await orchestrator.referPayment(paymentId, team, reason);
      setExpandedId(null);
      await refresh();
      showToast(`Payment referred to ${team}`);
    } catch (e) { showError(e.message); }
  };

  const getAge = (p, statusKey) => {
    const entry = p.statusHistory.find(h => h.status === (statusKey || 'INITIATED'));
    if (!entry?.at) return { label: '', days: 0 };
    const ms = Date.now() - new Date(entry.at).getTime();
    const mins = Math.floor(ms / 60000);
    const hrs = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    const label = mins < 60 ? `${mins}m ago` : hrs < 24 ? `${hrs}h ago` : `${days}d ago`;
    return { label, days };
  };

  const renderCard = (p, section) => {
    const initiator = p.statusHistory.find(h => h.status === 'INITIATED')?.by || '—';
    const sourceAcc = p.sourceAccountId ? ledger.getAccount(p.sourceAccountId) : null;
    const isPortalPayment = p.status === 'INITIATED' && p.metadata?.via === 'customer-portal';
    const isExpanded = expandedId === p.id;
    const isActionable = section === 'actionable';
    const isReferred = section === 'referred';

    const borderColor = isReferred ? T.purpleBorder : isActionable ? T.amberBorder : T.border;

    return (
      <Card key={p.id} style={{ border: `1px solid ${borderColor}`, opacity: isActionable || isReferred ? 1 : 0.8, borderLeft: isReferred ? `3px solid ${T.purple}` : undefined }}>
        <div style={{ cursor: 'pointer' }} onClick={() => setExpandedId(isExpanded ? null : p.id)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{p.beneficiary?.name || 'Unknown'}</div>
              <div style={{ fontSize: 11, color: T.textMuted, fontFamily: mono }}>{p.reference}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: isActionable ? 18 : 16, fontWeight: 700, fontFamily: mono }}>{fmtCcy(p.amount, p.currency)}</div>
              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                <Badge color={schemeColor(p.scheme)}>{p.scheme}</Badge>
                {isPortalPayment && <Badge color="blue">Portal</Badge>}
                {isReferred && <Badge color="purple">Referred</Badge>}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, fontSize: 12, color: T.textSecondary }}>
            <div>From: <span style={{ fontWeight: 500, color: T.text }}>{sourceAcc?.name || sourceAcc?.accountNumber || '—'}</span></div>
            <div>Initiated by: <span style={{ fontWeight: 500, color: T.text }}>{initiator}</span></div>
            <div>Fee: <span style={{ fontFamily: mono }}>{p.feeAmount > 0 ? fmtCcy(p.feeAmount, p.currency) : 'None'}</span></div>
            {isActionable && !isPortalPayment && <div>Threshold: <span style={{ fontFamily: mono }}>{fmtCcy(p.approvalThreshold || 0)}</span></div>}
            {isPortalPayment && <div>Status: <span style={{ fontWeight: 500, color: T.amber }}>Awaiting bank review</span></div>}
            {section === 'own' && (() => { const age = getAge(p); const overdue = age.days > 5; return (
              <div style={{ color: overdue ? T.red : T.amber, fontWeight: 500 }}>
                Awaiting second authoriser{overdue ? ` (${age.days} days — overdue)` : ''} · <span style={{ fontWeight: 400, color: T.textMuted }}>Submitted {age.label}</span>
              </div>
            ); })()}
            {isReferred && (() => { const age = getAge(p, 'REFERRED'); return (
              <div style={{ color: T.purple, fontWeight: 500 }}>
                Referred to {p.referral?.team} by {p.referral?.referredBy} · <span style={{ fontWeight: 400, color: T.textMuted }}>{age.label}</span>
              </div>
            ); })()}
            <div style={{ marginLeft: 'auto', color: T.textMuted, fontSize: 11 }}>{isExpanded ? '▲' : '▼'}</div>
          </div>
        </div>

        {isExpanded && !isReferred && (
          <ApprovalExpandedPanel
            payment={p} ledger={ledger} beneficiaries={beneficiaries} allPayments={allPayments}
            section={section} onApprove={handleApprove} onReject={handleReject} onRefer={handleRefer}
          />
        )}
        {isExpanded && isReferred && (
          <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 12, paddingTop: 12 }}>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              <span style={{ fontWeight: 600, color: T.purple }}>Referred to {p.referral?.team}</span> by {p.referral?.referredBy}
            </div>
            <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>Reason: {p.referral?.reason}</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>No action required — {p.referral?.team} team will review.</div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div>
      <ST>Payment Approval Queue</ST>
      <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>
        Payments requiring bank review: customer-portal payments above the bank approval threshold, and payments above the dual-authorisation threshold requiring a second RM to approve.
      </p>

      <Card style={{ marginBottom: 16, padding: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: T.textSecondary }}>Approval Thresholds by Scheme</div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {Object.entries(SCHEME_CONFIG).filter(([, v]) => v.approvalThreshold).map(([scheme, config]) => (
            <div key={scheme} style={{ fontSize: 12 }}>
              <Badge color={schemeColor(scheme)}>{scheme}</Badge>{' '}
              <span style={{ fontFamily: mono, fontWeight: 600 }}>≥ {fmtCcy(config.approvalThreshold)}</span>
            </div>
          ))}
        </div>
      </Card>

      {pending.length === 0 && referred.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>No payments pending approval</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>All payments are within auto-approval limits</div>
        </Card>
      ) : (
        <>
          <ST>Awaiting Your Authorisation ({actionable.length})</ST>
          {actionable.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: 24, marginBottom: 24 }}>
              <div style={{ fontSize: 13, color: T.textMuted }}>No payments awaiting your authorisation</div>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              {actionable.map(p => renderCard(p, 'actionable'))}
            </div>
          )}

          {own.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <ST>Your Payments Pending Authorisation ({own.length})</ST>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {own.map(p => renderCard(p, 'own'))}
              </div>
            </div>
          )}

          {referred.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <ST>Referred for Review ({referred.length})</ST>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {referred.map(p => renderCard(p, 'referred'))}
              </div>
            </div>
          )}
        </>
      )}

      {recentApproved.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <ST>Recently Approved</ST>
          <DataTableCompact
            headers={['Ref', 'Scheme', 'Amount', 'Initiated By', 'Approved By', 'Status']}
            rows={recentApproved.map(p => [
              <span style={{ fontWeight: 600, fontSize: 11, fontFamily: mono }}>{p.reference}</span>,
              <Badge color={schemeColor(p.scheme)}>{p.scheme}</Badge>,
              <span style={{ fontWeight: 600, fontFamily: mono }}>{fmtCcy(p.amount, p.currency)}</span>,
              <span style={{ fontSize: 12 }}>{p.statusHistory.find(h => h.status === 'INITIATED')?.by || '—'}</span>,
              <span style={{ fontSize: 12, color: T.green }}>{p.approvedBy || '—'}</span>,
              <Badge color={pmtStatusColor(p.status)}>{p.status}</Badge>,
            ])}
          />
        </Card>
      )}
    </div>
  );
}


// ============================================================
// BENEFICIARIES — Saved payee management
// ============================================================

function BeneficiariesView({ beneficiaries, refresh, showToast, showError }) {
  const allBens = beneficiaries.getAll();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', nickname: '', sortCode: '', accountNumber: '', bic: '', iban: '', scheme: 'FPS' });

  const handleAdd = async () => {
    try {
      await beneficiaries.add({
        name: form.name, nickname: form.nickname || form.name,
        sortCode: form.sortCode, accountNumber: form.accountNumber,
        bic: form.bic, iban: form.iban, scheme: form.scheme,
        createdBy: 'rm_sarah_j',
      });
      await refresh();
      showToast(`Beneficiary "${form.nickname || form.name}" saved`);
      setForm({ name: '', nickname: '', sortCode: '', accountNumber: '', bic: '', iban: '', scheme: 'FPS' });
      setShowForm(false);
    } catch (e) { showError(e.message); }
  };

  const handleDeactivate = async (id) => {
    await beneficiaries.deactivate(id);
    await refresh();
    showToast('Beneficiary deactivated');
  };

  const schemeConfig = SCHEME_CONFIG[form.scheme];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ST>Saved Beneficiaries</ST>
        <Btn onClick={() => setShowForm(!showForm)} small>{showForm ? 'Cancel' : '+ Add Beneficiary'}</Btn>
      </div>

      <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>
        Saved beneficiaries can be pre-filled when creating payments. In production, new beneficiaries would be verified via Confirmation of Payee (CoP) through Pay.UK.
      </p>

      {showForm && (
        <Card style={{ marginBottom: 16, border: `1px solid ${T.blueBorder}` }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>New Beneficiary</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Inp label="Legal Name" value={form.name} onChange={v => setForm({ ...form, name: v })} placeholder="Company Ltd" />
              <Inp label="Nickname" value={form.nickname} onChange={v => setForm({ ...form, nickname: v })} placeholder="Short name for quick select" />
              <Sel label="Primary Scheme" value={form.scheme} onChange={v => setForm({ ...form, scheme: v })}
                options={Object.entries(PaymentScheme).map(([k, v]) => ({ value: v, label: `${v} — ${SCHEME_CONFIG[v].name}` }))} />
            </div>
            <div style={{ flex: 1 }}>
              {(schemeConfig?.requiresSortCode || form.scheme === 'INTERNAL') && <Inp label="Sort Code" value={form.sortCode} onChange={v => setForm({ ...form, sortCode: v })} placeholder="090128" />}
              {(schemeConfig?.requiresAccountNumber || form.scheme === 'INTERNAL') && <Inp label="Account Number" value={form.accountNumber} onChange={v => setForm({ ...form, accountNumber: v })} placeholder="31926819" />}
              {schemeConfig?.requiresBIC && <Inp label="BIC" value={form.bic} onChange={v => setForm({ ...form, bic: v })} placeholder="LOYDGB2L" />}
              {schemeConfig?.requiresIBAN && <Inp label="IBAN" value={form.iban} onChange={v => setForm({ ...form, iban: v })} placeholder="GB29NWBK60161331926819" />}
            </div>
          </div>
          <Btn onClick={handleAdd} disabled={!form.name}>Save Beneficiary</Btn>
        </Card>
      )}

      {allBens.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 14 }}>No saved beneficiaries yet</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Add beneficiaries to speed up payment creation</div>
        </Card>
      ) : (
        <Card>
          <DataTableCompact
            headers={['Nickname', 'Legal Name', 'Scheme', 'Account Details', 'Last Used', 'Uses', '']}
            rows={allBens.map(b => [
              <span style={{ fontWeight: 600 }}>{b.nickname}</span>,
              <span style={{ fontSize: 12, color: T.textSecondary }}>{b.name}</span>,
              <Badge color={schemeColor(b.scheme)}>{b.scheme}</Badge>,
              <span style={{ fontFamily: mono, fontSize: 11 }}>
                {b.sortCode && `${b.sortCode.slice(0,2)}-${b.sortCode.slice(2,4)}-${b.sortCode.slice(4)} `}
                {b.accountNumber || ''}
                {b.bic && `BIC: ${b.bic} `}
                {b.iban && `IBAN: ${b.iban.slice(0, 8)}…`}
              </span>,
              <span style={{ fontSize: 11, color: T.textMuted }}>{b.lastUsed ? fmtDate(b.lastUsed) : 'Never'}</span>,
              <span style={{ fontFamily: mono }}>{b.useCount}</span>,
              <Btn onClick={() => handleDeactivate(b.id)} variant="ghost" small style={{ color: T.red }}>Remove</Btn>,
            ])}
          />
        </Card>
      )}
    </div>
  );
}


// ============================================================
// STANDING ORDERS
// ============================================================

function StandingOrdersView({ standingOrders, orchestrator, ledger, beneficiaries, refresh, showToast, showError }) {
  const allOrders = standingOrders.getAll();
  const custAccs = ledger.getAllAccounts({ internal: false }).filter(a => a.status === AccountStatus.ACTIVE);
  const savedBens = beneficiaries ? beneficiaries.getAll() : [];
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    sourceAccountId: '', beneficiaryName: '', sortCode: '', accountNumber: '',
    amount: '', scheme: 'FPS', reference: '', frequency: 'MONTHLY', startDate: new Date().toISOString().split('T')[0],
    endDate: '', maxExecutions: '', savedBeneficiaryId: '',
  });

  const handleSelectBeneficiary = (benId) => {
    if (!benId) { setForm({ ...form, savedBeneficiaryId: '', beneficiaryName: '', sortCode: '', accountNumber: '' }); return; }
    const ben = beneficiaries.get(benId);
    if (ben) setForm({ ...form, savedBeneficiaryId: benId, beneficiaryName: ben.name, sortCode: ben.sortCode || '', accountNumber: ben.accountNumber || '', scheme: ben.scheme || form.scheme });
  };

  const handleCreate = async () => {
    try {
      await standingOrders.create({
        sourceAccountId: form.sourceAccountId,
        beneficiary: { name: form.beneficiaryName, sortCode: form.sortCode?.replace(/-/g, ''), accountNumber: form.accountNumber },
        amount: parseFloat(form.amount),
        currency: ledger.getAccount(form.sourceAccountId)?.currency || 'GBP',
        scheme: form.scheme,
        reference: form.reference || `SO-${Date.now()}`,
        frequency: form.frequency,
        startDate: form.startDate,
        endDate: form.endDate || null,
        maxExecutions: form.maxExecutions ? parseInt(form.maxExecutions) : null,
        createdBy: 'rm_sarah_j',
      });
      await refresh();
      showToast('Standing order created');
      setShowForm(false);
    } catch (e) { showError(e.message); }
  };

  const handleExecute = async (orderId) => {
    try {
      const result = await standingOrders.execute(orderId);
      await refresh();
      if (result.success) showToast(`Standing order executed: ${fmtCcy(result.payment.amount, result.payment.currency)}`);
      else showError(`Execution failed: ${result.error || result.reason}`);
    } catch (e) { showError(e.message); }
  };

  const handlePauseResume = async (order) => {
    try {
      if (order.status === SOStatus.ACTIVE) await standingOrders.pause(order.id);
      else if (order.status === SOStatus.PAUSED) await standingOrders.resume(order.id);
      await refresh();
    } catch (e) { showError(e.message); }
  };

  const handleCancel = async (orderId) => {
    try { await standingOrders.cancel(orderId, 'Cancelled by RM'); await refresh(); showToast('Standing order cancelled'); } catch (e) { showError(e.message); }
  };

  const soStatusColor = s => ({ ACTIVE: 'green', PAUSED: 'amber', CANCELLED: 'red', COMPLETED: 'gray' }[s] || 'gray');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ST>Standing Orders</ST>
        <Btn onClick={() => setShowForm(!showForm)} small>{showForm ? 'Cancel' : '+ New Standing Order'}</Btn>
      </div>

      <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>
        Recurring payments executed on schedule. In production, the EOD engine processes due standing orders automatically each business day. Standing orders bypass the dual-authorisation requirement as they are pre-approved at creation.
      </p>

      {showForm && (
        <Card style={{ marginBottom: 16, border: `1px solid ${T.blueBorder}` }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>New Standing Order</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Sel label="Source Account" value={form.sourceAccountId} onChange={v => setForm({ ...form, sourceAccountId: v })}
                options={[{ value: '', label: 'Select…' }, ...custAccs.map(a => ({ value: a.id, label: `${a.accountNumber} — ${a.name}` }))]} />
              <Inp label="Amount" type="number" value={form.amount} onChange={v => setForm({ ...form, amount: v })} placeholder="0.00" />
              <Inp label="Reference" value={form.reference} onChange={v => setForm({ ...form, reference: v })} placeholder="RENT-MONTHLY" />
              <Sel label="Frequency" value={form.frequency} onChange={v => setForm({ ...form, frequency: v })}
                options={Object.entries(SOFrequency).map(([k, v]) => ({ value: v, label: v }))} />
            </div>
            <div style={{ flex: 1 }}>
              <Inp label="Start Date" type="date" value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
              <Inp label="End Date (optional)" type="date" value={form.endDate} onChange={v => setForm({ ...form, endDate: v })} />
              <Inp label="Max Executions (optional)" type="number" value={form.maxExecutions} onChange={v => setForm({ ...form, maxExecutions: v })} placeholder="∞" />
              {savedBens.length > 0 && (
                <Sel label="Saved Beneficiary" value={form.savedBeneficiaryId} onChange={handleSelectBeneficiary}
                  options={[{ value: '', label: 'Enter manually…' }, ...savedBens.map(b => ({ value: b.id, label: b.nickname }))]} />
              )}
              <Inp label="Beneficiary Name" value={form.beneficiaryName} onChange={v => setForm({ ...form, beneficiaryName: v })} placeholder="Landlord Ltd" />
              <Inp label="Sort Code" value={form.sortCode} onChange={v => setForm({ ...form, sortCode: v })} placeholder="090128" />
              <Inp label="Account Number" value={form.accountNumber} onChange={v => setForm({ ...form, accountNumber: v })} placeholder="31926819" />
            </div>
          </div>
          <Btn onClick={handleCreate} disabled={!form.sourceAccountId || !form.amount || !form.beneficiaryName}>Create Standing Order</Btn>
        </Card>
      )}

      {allOrders.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 14 }}>No standing orders</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Create a standing order for recurring payments</div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {allOrders.map(o => {
            const sourceAcc = o.sourceAccountId ? ledger.getAccount(o.sourceAccountId) : null;
            return (
              <Card key={o.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{o.beneficiary?.name || 'Unknown'}</div>
                    <div style={{ fontSize: 11, color: T.textMuted, fontFamily: mono }}>{o.reference} · From: {sourceAcc?.accountNumber || '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, fontFamily: mono }}>{fmtCcy(o.amount, o.currency)}</div>
                    <Badge color={soStatusColor(o.status)}>{o.status}</Badge>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 20, fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>
                  <div>Frequency: <span style={{ fontWeight: 500, color: T.text }}>{o.frequency}</span></div>
                  <div>Next: <span style={{ fontWeight: 500, color: T.text }}>{o.nextExecutionDate}</span></div>
                  <div>Executed: <span style={{ fontFamily: mono }}>{o.executionCount}{o.maxExecutions ? ` / ${o.maxExecutions}` : ''}</span></div>
                  {o.lastExecutedAt && <div>Last: <span style={{ fontSize: 11 }}>{fmtDateTime(o.lastExecutedAt)}</span></div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {o.status === SOStatus.ACTIVE && <Btn onClick={() => handleExecute(o.id)} small>Execute Now</Btn>}
                  {(o.status === SOStatus.ACTIVE || o.status === SOStatus.PAUSED) && (
                    <Btn onClick={() => handlePauseResume(o)} variant="secondary" small>
                      {o.status === SOStatus.ACTIVE ? 'Pause' : 'Resume'}
                    </Btn>
                  )}
                  {o.status !== SOStatus.CANCELLED && o.status !== SOStatus.COMPLETED && (
                    <Btn onClick={() => handleCancel(o.id)} variant="danger" small>Cancel</Btn>
                  )}
                </div>
                {o.history.length > 0 && (
                  <div style={{ marginTop: 8, borderTop: `1px solid ${T.borderLight}`, paddingTop: 8 }}>
                    <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Recent History</div>
                    {o.history.slice(-3).reverse().map((h, i) => (
                      <div key={i} style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>
                        {fmtDateTime(h.at)} — {h.action}{h.status ? ` (${h.status})` : ''}{h.error ? ` — ${h.error}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================
// NOSTRO RECONCILIATION
// ============================================================

function NostroReconView({ orchestrator, ledger }) {
  const nostros = orchestrator.getNostroSummary();
  const payments = orchestrator.getAllPayments();
  const returned = payments.filter(p => p.status === PaymentStatus.RETURNED);
  const [selectedCcy, setSelectedCcy] = useState(null);

  // Get settlement entries for selected nostro
  const getSettlementEntries = (ccy) => {
    return payments
      .filter(p => p.currency === ccy && p.settlementTxnId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 20);
  };

  return (
    <div>
      <ST>Nostro Reconciliation</ST>
      <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>
        Nostro accounts represent our bank's position with correspondent banks. In production, these balances are reconciled daily against scheme confirmation files (MT940 for SWIFT, settlement reports for FPS/CHAPS/BACS).
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {nostros.map(n => (
          <Card key={n.currency} style={{ flex: 1, minWidth: 280, cursor: 'pointer', border: selectedCcy === n.currency ? `2px solid ${T.blue}` : `1px solid ${T.border}` }} onClick={() => setSelectedCcy(selectedCcy === n.currency ? null : n.currency)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary }}>{n.currency} Nostro</div>
                <div style={{ fontSize: 10, color: T.textMuted, fontFamily: mono }}>{n.accountNumber}</div>
              </div>
              <Badge color={n.balance >= 0 ? 'green' : 'red'}>{n.currency}</Badge>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, marginBottom: 8 }}>{fmtCcy(n.balance, n.currency)}</div>
            <div style={{ display: 'flex', gap: 16, fontSize: 11 }}>
              <div><span style={{ color: T.green }}>▲</span> In: {fmtCcy(n.totalInbound, n.currency)} ({n.inboundCount})</div>
              <div><span style={{ color: T.red }}>▼</span> Out: {fmtCcy(n.totalOutbound, n.currency)} ({n.outboundCount})</div>
            </div>
            {n.inFlightCount > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: T.amber }}>
                In flight: {n.inFlightCount} payments ({fmtCcy(n.inFlightAmount, n.currency)})
              </div>
            )}
          </Card>
        ))}
      </div>

      {nostros.length === 0 && (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 14 }}>No nostro activity</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Process some payments to see nostro positions</div>
        </Card>
      )}

      {selectedCcy && (
        <Card>
          <ST>{selectedCcy} Settlement Activity (Recent 20)</ST>
          <DataTableCompact
            headers={['Ref', 'Dir', 'Scheme', 'Amount', 'Status', 'Counterparty', 'Time']}
            rows={getSettlementEntries(selectedCcy).map(p => [
              <span style={{ fontWeight: 600, fontSize: 11, fontFamily: mono }}>{p.reference}</span>,
              <Badge color={dirColor(p.direction)}>{p.direction}</Badge>,
              <Badge color={schemeColor(p.scheme)}>{p.scheme}</Badge>,
              <span style={{ fontWeight: 600, fontFamily: mono }}>{fmtCcy(p.amount, p.currency)}</span>,
              <Badge color={pmtStatusColor(p.status)}>{p.status}</Badge>,
              <span style={{ fontSize: 12, color: T.textSecondary }}>{p.beneficiary?.name || p.sender?.name || '—'}</span>,
              <span style={{ fontSize: 10, color: T.textMuted }}>{fmtDateTime(p.createdAt)}</span>,
            ])}
          />
        </Card>
      )}

      {returned.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <ST>Returned Payments</ST>
          <DataTableCompact
            headers={['Ref', 'Dir', 'Scheme', 'Amount', 'Reason', 'Time']}
            rows={returned.map(p => [
              <span style={{ fontWeight: 600, fontSize: 11, fontFamily: mono }}>{p.reference}</span>,
              <Badge color={dirColor(p.direction)}>{p.direction}</Badge>,
              <Badge color={schemeColor(p.scheme)}>{p.scheme}</Badge>,
              <span style={{ fontWeight: 600, fontFamily: mono }}>{fmtCcy(p.returnedAmount || p.amount, p.currency)}</span>,
              <span style={{ fontSize: 12, color: T.red }}>{p.returnReason || '—'}</span>,
              <span style={{ fontSize: 10, color: T.textMuted }}>{fmtDateTime(p.updatedAt)}</span>,
            ])}
          />
        </Card>
      )}
    </div>
  );
}


// ============================================================
// EVENTS
// ============================================================

function PaymentEventsView({ eventBus }) {
  const events = eventBus ? [...eventBus.eventLog].filter(e => e.type.includes('Payment') || e.type.includes('Hold') || e.type.includes('Approval')).reverse().slice(0, 50) : [];
  const eventColor = (type) => {
    if (type.includes('Settled')) return 'green';
    if (type.includes('Initiated')) return 'amber';
    if (type.includes('Hold')) return 'purple';
    if (type.includes('Approved') || type.includes('Approval')) return 'blue';
    if (type.includes('Rejected') || type.includes('Failed')) return 'red';
    if (type.includes('Returned')) return 'amber';
    if (type.includes('Awaiting')) return 'amber';
    return 'gray';
  };
  return (
    <div>
      <ST>Payment Event Stream</ST>
      <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>Payment-related events from the shared event bus.</p>
      <Card>
        <div style={{ maxHeight: 500, overflow: 'auto' }}>
          {events.length === 0
            ? <div style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>No payment events yet.</div>
            : events.map((e, i) => (
              <div key={i} style={{ padding: '8px 10px', borderBottom: `1px solid ${T.borderLight}`, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <Badge color={eventColor(e.type)}>{e.type}</Badge>
                  <span style={{ fontSize: 10, color: T.textMuted }}>{new Date(e.timestamp).toLocaleString()}</span>
                </div>
                <div style={{ fontSize: 10, color: T.textMuted, fontFamily: mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{JSON.stringify(e.data).slice(0, 200)}</div>
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}


// ============================================================
